import L from 'leaflet';

/**
 * LocationsMap markers: a pin (blue default, green selected) and a standalone
 * amber target icon. Styles: src/styles/global/locations-map-marker.less
 */

const PIN_WIDTH = 28;
const PIN_HEIGHT = 31;
const TARGET_SIZE = 26;

export const LOCATIONS_MAP_MARKER_COLORS = {
  default: '#1890ff',
  selected: '#52c41a',
  target: '#fa8c16',
} as const;

export type TLocationsMapMarkerVariant = keyof typeof LOCATIONS_MAP_MARKER_COLORS;

type TCreateLocationsMapMarkerIconOptions = {
  variant?: TLocationsMapMarkerVariant;
  /** Caption rendered under the marker; already translated. */
  label?: string;
};

export type TMarkerLayout = {
  iconSize: [number, number];
  iconAnchor: [number, number];
  popupAnchor: [number, number];
  tooltipAnchor: [number, number];
};

const PIN_BODY_PATH =
  'M14 1C7.925 1 3 5.925 3 12c0 8.25 11 18 11 18s11-14.75 11-18C25 5.925 20.075 1 14 1z';
const PIN_HOLE_PATH = 'M14 12m-4.5 0a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0';

/** Material `adjust`, drawn on its own (no pin) so it marks the exact spot. */
const ADJUST_PATH =
  'M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3-8c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z';

const SVG_ATTRIBUTES = 'xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"';

const createPinSvg = (variant: TLocationsMapMarkerVariant) => `
  <svg ${SVG_ATTRIBUTES} width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="0 0 28 31">
    <path
      fill-rule="evenodd"
      d="${PIN_BODY_PATH} ${PIN_HOLE_PATH}"
      fill="${LOCATIONS_MAP_MARKER_COLORS[variant]}"
    />
  </svg>
`;

const createTargetSvg = () => `
  <svg ${SVG_ATTRIBUTES} width="${TARGET_SIZE}" height="${TARGET_SIZE}" viewBox="0 0 24 24">
    <path d="${ADJUST_PATH}" fill="${LOCATIONS_MAP_MARKER_COLORS.target}" />
  </svg>
`;

export const LOCATIONS_MAP_MARKER_LAYOUT: TMarkerLayout = {
  iconSize: [PIN_WIDTH, PIN_HEIGHT],
  iconAnchor: [PIN_WIDTH / 2, PIN_HEIGHT],
  popupAnchor: [0, -PIN_HEIGHT],
  tooltipAnchor: [0, -PIN_HEIGHT],
};

/** The target icon has no tip, so it is anchored at its center. */
export const TARGET_MARKER_LAYOUT: TMarkerLayout = {
  iconSize: [TARGET_SIZE, TARGET_SIZE],
  iconAnchor: [TARGET_SIZE / 2, TARGET_SIZE / 2],
  popupAnchor: [0, -TARGET_SIZE / 2],
  tooltipAnchor: [0, -TARGET_SIZE / 2],
};

export const LOCATIONS_MAP_MARKER_LABEL_STYLE = {
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  color: 'rgba(0, 0, 0, 0.55)',
  fontSize: 10,
  fontWeight: 500,
  lineHeight: 14,
  paddingX: 4,
  gap: 2,
  borderRadius: 3,
} as const;

export const createLocationsMapMarkerSvg = (variant: TLocationsMapMarkerVariant): string =>
  variant === 'target' ? createTargetSvg() : createPinSvg(variant);

export const getLocationsMapMarkerLayout = (
  variant: TLocationsMapMarkerVariant,
): TMarkerLayout =>
  variant === 'target' ? TARGET_MARKER_LAYOUT : LOCATIONS_MAP_MARKER_LAYOUT;

/** The label goes into `innerHTML`, so it is escaped by the platform. */
const toHtmlText = (value: string): string => {
  const element = document.createElement('span');
  element.textContent = value;

  return element.innerHTML;
};

const createLabelHtml = (label?: string) =>
  label ? `<span class="locations-map-marker-label">${toHtmlText(label)}</span>` : '';

export const createLocationsMapMarkerIcon = ({
  variant = 'default',
  label,
}: TCreateLocationsMapMarkerIconOptions = {}): L.DivIcon => {
  const isTarget = variant === 'target';
  const layout = getLocationsMapMarkerLayout(variant);
  const iconClassName = isTarget
    ? 'locations-map-marker-icon locations-map-marker-icon--target'
    : 'locations-map-marker-icon';
  const markerSvg = createLocationsMapMarkerSvg(variant);

  return L.divIcon({
    className: 'locations-map-marker-icon-wrapper',
    html: `<div class="${iconClassName}">${markerSvg}${createLabelHtml(label)}</div>`,
    ...layout,
  });
};
